import { noddiAsync, URLKeys } from 'noddi-async';
import { TireHotelAtOtherDepartmentDialog, useAuthContext } from 'noddi-web';
import { useStationaryBookableServices } from '../../../hooks/useStationaryBookableServices';
import { useStationaryGarages } from '../../../hooks/useStationaryGarages';
import {
  useServiceCenter,
  useStationaryBookingActions,
  useStationaryBookingCar,
  useTireHotelPromptDismissed
} from '../../../stores/StationaryBookingStore';
import { getCommonTranslations } from '../../../utils/commonTranslations';
import { tireHotelOtherDepartmentId } from '../../../utils/tireHotelDepartment';

type Props = {
  /** Runs after the store switched location (e.g. navigate back to the services step). */
  onSwitched?: () => void;
};

export const TireHotelAtOtherDepartmentPrompt = ({ onSwitched }: Props) => {
  // Privacy: the backend only returns tireHotelAtOtherDepartment to the car's
  // owner, and it must never render for anonymous sessions.
  const { isLoggedIn, currentUserGroupId } = useAuthContext();
  const dismissed = useTireHotelPromptDismissed();
  const { switchServiceCenter, dismissTireHotelPrompt } = useStationaryBookingActions();
  const serviceCenter = useServiceCenter();
  const selectedCar = useStationaryBookingCar();
  const { tireHotelAtOtherDepartment } = useStationaryBookableServices();

  // Adapter departments report the mismatch on bookable-services. Core
  // departments do not — fall back to the customer's hotel membership.
  const { data: tireHotels, error: _tireHotelsError } = noddiAsync.useGet({
    type: URLKeys.getTireHotelsForCustomer,
    input: {
      userGroupId: currentUserGroupId!,
      activeOnly: true
    },
    queryConfig: {
      enabled: isLoggedIn && !!currentUserGroupId && !!selectedCar
    }
  });

  const selectedCarHotel = selectedCar
    ? tireHotels?.find((hotel) => hotel.licensePlateNumber === selectedCar.licensePlate.number)
    : undefined;
  const hotelServiceDepartmentId = tireHotelOtherDepartmentId({
    bookableServicesOtherDepartmentId: tireHotelAtOtherDepartment?.id,
    hotelServiceDepartmentId: selectedCarHotel?.serviceDepartmentId,
    bookingServiceDepartmentId: serviceCenter?.serviceDepartmentId
  });
  const canShow = isLoggedIn && hotelServiceDepartmentId != null && !dismissed;
  const { garageOptions, isLoading } = useStationaryGarages({
    withCapacity: false,
    enabled: canShow
  });

  if (!canShow || isLoading || hotelServiceDepartmentId == null) {
    return null;
  }

  const targetGarage = garageOptions.find((garage) => garage.serviceDepartmentId === hotelServiceDepartmentId);
  const departmentName = tireHotelAtOtherDepartment?.name ?? targetGarage?.name;
  if (!departmentName) {
    return null;
  }

  const handleSwitch = () => {
    if (!targetGarage) {
      return;
    }
    // The held slot is freed by useReleaseStationaryHoldOnClear at the flow
    // root — switchServiceCenter nulls the reservation.
    switchServiceCenter(targetGarage);
    onSwitched?.();
  };

  return (
    <TireHotelAtOtherDepartmentDialog
      open
      departmentName={departmentName}
      canSwitch={targetGarage != null}
      translations={getCommonTranslations().tireHotelAtOtherDepartmentDialog}
      onContinueHere={dismissTireHotelPrompt}
      onSwitch={handleSwitch}
    />
  );
};
